import { useEffect, useRef, useState } from 'react';
import { ArrowUp, Check, Copy, Reply, X } from 'lucide-react';
import { Modal } from '../../Modal';
import { Avatar, Badge, IconButton, PageLoader, ScrollArea, Typography } from '../ui';
import { Markdown } from '../../Markdown';
import { MaestroMark } from '../MaestroMark';
import { ChannelChip } from './ChannelChip';
import { ContactReadError } from './ContactReadError';
import { OutreachNoReplyNeeded } from '../tasks/OutreachNoReplyNeeded';
import { avatarInitials, avatarSlotFor } from '../../../lib/avatar-color';
import { appendOptimisticOutreachMessage, refreshContactData, useContactEntryQuery, type ContactId, type WebOutreachMessage } from '../../../lib/contact-book';
import { sendOutreachMessage } from '../../../lib/outreach-send-message';
import { isImeComposing } from '../../../lib/ime-composition';
import { contactDisplayName, findOutreach, messageIsMine, shortRelativeTime, startsSenderRun, strippedMessageText, threadChannel, threadTurn } from '../../../lib/contact-rows';
import { MessageAttachments } from './ContactThreadBody';
import { useAgentName } from '../../../lib/use-agent-name';
import { useMe } from '../../../lib/me';
import { useNarrow } from '../../../lib/use-narrow';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useLoadBi } from '../../../lib/use-load-bi';
import styles from './contacts.module.css';

type TFn = (key: string, opts?: Record<string, unknown>) => string;
type LogClick = (params: { page_name: string; button_name: string }) => void;

/**
 * The outreach conversation viewer — ONE OUTREACH, in a dialog (a sheet on narrow), opened from a
 * contact row whose contact has a single conversation, from a conversation row on the contact screen,
 * or from an Inbox row.
 *
 * It used to be keyed on a CHANNEL and stitched every outreach on that channel into one scroll. That
 * read as a single conversation while being several: a reply landed on whichever of them was still
 * open, a closed sibling could disable the composer on a live thread, and the older ones were
 * unreachable from any list. A conversation is an outreach; a channel is only how it was delivered.
 *
 * It renders the correspondence as a chat (opposing bubbles, the sender named once per run, the inbound
 * side carrying reply/copy) and answers it with a text-only composer. Sending does NOT put raw text on
 * the channel — Symphony has no such path, by design. What the user types is an INSTRUCTION seed
 * (`outreachReplyMessage`: "Reply to {name} on the outreach '{subject}'. Say: {body}"), and on send the
 * viewer closes and hands that seed to a fresh chat that autosends it. The agent composes, confirms and
 * relays it on the channel there — the same shipped handoff `ContactThreadBody` uses. This is why the
 * confirmation happens in chat, not in the sheet: the harness deliberately requires the agent to
 * preview/approve a send, and there is no client-settable approval to skip it. The chat opens IN PLACE
 * (`useOpenSideChat`) — the dialog closes but the underlying screen (contacts list / contact detail)
 * stays put; we do NOT navigate to home (a `chatHandoffLink` route push would).
 *
 * Emoji reactions are deliberately absent: an outreach message has no id and no reaction backend to
 * persist one to, so a react would vanish on the next read. Reply (quote) and Copy — which need no
 * backend — are kept, matching the regular chat.
 */
export function ContactOutreachViewer({
  personId,
  recipientId,
  outreachId,
  open,
  onClose,
  onOpenContact,
}: {
  personId: string;
  /** Set (with an empty `personId`) for a contact the people book knows only by an address — the
   *  conversation is then read by recipient id, exactly as the recipient-keyed Contacts detail is. */
  recipientId?: string;
  /** THE ONE OUTREACH to open. It used to be a channel key, and the viewer stitched every outreach on
   *  that channel into a single scroll — which read as one conversation while being several, so a
   *  reply landed on whichever of them happened to still be open and the others were unreachable.
   *  A conversation is an outreach; the channel is only how it was delivered. */
  outreachId: string;
  open: boolean;
  onClose: () => void;
  /** Present when opened from the contacts LIST — pressing the header navigates to the contact screen.
   *  Absent when opened FROM the contact screen (pressing the header does nothing). */
  onOpenContact?: () => void;
}) {
  const { t } = useTranslation();
  const { logPageClickBi, logModalActionBi } = useGenericBi();
  const narrow = useNarrow();
  // Person or address-only identity — the read is keyed on whichever the row carried.
  const contactId: ContactId = personId ? { personId } : { recipientId: recipientId ?? '' };
  const { entry, isLoading, isError, refetch } = useContactEntryQuery(open ? contactId : { personId: '' });
  const me = useMe();
  const agent = useAgentName();

  // A contact the people book knows only by an address (recipientId, no personId) is the #10660 path.
  const contactKind = personId ? 'person' : 'recipient';
  // Opened FROM the contacts/inbox LIST (the header can navigate to the record) reports `contacts`;
  // opened from the contact detail card (no navigation target) reports `contact_detail`.
  const page = onOpenContact ? 'contacts' : 'contact_detail';

  // The dialog reports itself opening, once per opening — the modal event the trigger row's click
  // (open_contact / open_inbox_row / needs_you_channel) cannot stand in for.
  useEffect(() => {
    if (!open) return;
    logModalActionBi(
      { page_name: page, modal_name: 'open_conversation_viewer', action: 'open' },
      { additionalFields: { contact_kind: contactKind } },
    );
    // Keyed on `open`: a re-read or a step inside the dialog must not re-report it as a new opening.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // The contact read fetches on open (whole threads, not the list preview) — track it as a load region.
  // Drive it off the query's REAL state, not `!entry`: on a fetch error `entry` stays undefined, so
  // `!entry` would sit "loading" forever and loadFinish would never fire. `isLoading` settles on both
  // success and failure, and `isError` carries the outcome into `isSuccessful`.
  useLoadBi(
    'contact-outreach-viewer',
    { isLoading, isError },
    { enabled: open, additionalFields: { contact_kind: contactKind } },
  );

  if (!open) return null;

  // THIS outreach, and only it. Its own messages, in the order the service recorded them.
  const thread = entry ? findOutreach(entry, outreachId) : undefined;
  // Through the shared rule, NOT the raw field: an in-app thread's channel is empty on the wire, and
  // `ChannelChip` draws nothing for an empty value — so the header silently lost its badge while the
  // rows behind it kept theirs.
  const channel = thread ? threadChannel(thread) : '';
  const messages = (thread?.messages ?? []).slice().sort((a, b) => sentMs(a.sentAt) - sentMs(b.sentAt));
  const turn = thread ? threadTurn(thread) : undefined;
  const waitingOnMe = turn === 'needsYou';
  // The reply lands on the outreach being read — there is no longer a set of them to choose a target
  // from, and a CLOSED one is no longer refused: the server REOPENS it. `send-outreach-message`
  // advances the status from its post-send read (#10932, #10934), so answering a finished conversation
  // is how you continue it, and the thread comes back as IN_PROGRESS with the new message in it.
  //
  // The gate that used to sit here was a client-side invention — the endpoint never had a status check
  // — and it made "Replied" mean "you may not write to this person again", which is not what closing a
  // thread means. COMPLETED means no work pending, not closed forever.
  const replyTarget = outreachId;
  // Same fallback the list row and detail header use, so the viewer's header, initials and "them"
  // attribution name an address-only / nameless contact by its address rather than a blank line.
  const theirName = contactDisplayName(entry?.contact, entry?.channels, t('contacts.row.unnamed'));
  const myName = (me?.recipientName || [me?.firstName, me?.lastName].filter(Boolean).join(' ')).trim();

  return (
    <Modal
      open
      onClose={onClose}
      size="small"
      maxHeightPx={650}
      contentClassName={styles.viewerContentSlot}
      presentation={narrow ? 'sheet' : 'centered'}
      labelledBy="outreach-viewer-title"
    >
      <ViewerContent
        personId={personId}
        recipientId={recipientId}
        channel={channel}
        outreachId={replyTarget}
        threadOutreachId={outreachId}
        settled={turn === 'closed'}
        onSettled={onClose}
        contactId={contactId}
        messages={messages}
        waitingOnMe={waitingOnMe}
        loading={isLoading}
        failed={!isLoading && !entry}
        canRetry={isError}
        onRetry={refetch}
        theirName={theirName}
        theirPhoto={entry?.contact?.effectiveAvatarUrl}
        myName={myName}
        myAgent={agent.short}
        t={t as unknown as TFn}
        logClick={logPageClickBi as unknown as LogClick}
        onOpenContact={onOpenContact}
      />
    </Modal>
  );
}

function sentMs(at: string | undefined): number {
  if (!at) return 0;
  const ms = new Date(at).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function ViewerContent({
  personId,
  recipientId,
  channel,
  outreachId,
  threadOutreachId,
  settled,
  onSettled,
  contactId,
  messages,
  waitingOnMe,
  loading,
  failed,
  canRetry,
  onRetry,
  theirName,
  theirPhoto,
  myName,
  myAgent,
  t,
  logClick,
  onOpenContact,
}: {
  personId: string;
  recipientId?: string;
  channel: string;
  /** The REPLY target — empty only when there is no outreach to send against. */
  outreachId: string;
  /** The conversation itself, and whether it is already settled: the "Mark as replied" action is the
   *  way to finish a thread WITHOUT answering it, so it is offered only while something is open. */
  threadOutreachId: string;
  settled: boolean;
  onSettled: () => void;
  contactId: ContactId;
  messages: WebOutreachMessage[];
  waitingOnMe: boolean;
  loading: boolean;
  /**
   * The read SETTLED with no contact to show — keyed on the ENTRY, not on the message count.
   *
   * Message count was the wrong predicate in both directions. A query that settles successfully with
   * `contacts: []` (a stale or deleted list row) leaves `isError` false, so a zero-message test let the
   * sheet render an empty transcript over a live composer — a reply against a contact that never
   * loaded. And a genuinely EMPTY conversation whose refresh failed has zero messages too, so it was
   * misclassified as a failure and shown an error instead of its real empty self.
   */
  failed: boolean;
  /** Whether retrying can help: a real error, as opposed to a read that succeeded and carried nothing. */
  canRetry: boolean;
  onRetry: () => void;
  theirName: string;
  theirPhoto?: string;
  myName: string;
  myAgent: string;
  t: TFn;
  logClick: LogClick;
  onOpenContact?: () => void;
}) {
  const [reply, setReply] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);

  // Open at the newest message — a correspondence is read at its end.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [loading, messages.length]);

  const headerPressable = Boolean(onOpenContact);
  const openContact = () => {
    if (!onOpenContact) return;
    logClick({ page_name: 'contact_thread', button_name: 'open_contact_from_viewer' });
    onOpenContact();
  };

  return (
    <div className={styles.viewer}>
      <header className={styles.viewerHeader}>
        <button type="button" className={headerPressable ? styles.viewerIdentityPressable : styles.viewerIdentity} onClick={openContact} disabled={!headerPressable}>
          <Avatar initials={avatarInitials(theirName)} photoUrl={theirPhoto} size={40} color={avatarSlotFor(personId || theirName)} />
          <span className={styles.viewerIdentityText}>
            <Typography variant="h3" id="outreach-viewer-title" className={styles.viewerName}>
              {theirName}
            </Typography>
            <ChannelChip channel={channel} />
          </span>
        </button>
      </header>

      <ScrollArea className={styles.viewerThread}>
        {loading ? (
          <PageLoader />
        ) : failed ? (
          <ContactReadError canRetry={canRetry} onRetry={onRetry} />
        ) : (
          <>
            {messages.map((message, index) => (
              <MessageBubble
                key={`m-${index}`}
                message={message}
                mine={messageIsMine(message, myName)}
                showSender={startsSenderRun(messages, index)}
                awaitsYou={waitingOnMe && index === messages.length - 1 && !messageIsMine(message, myName)}
                theirName={theirName}
                theirPhoto={theirPhoto}
                myAgent={myAgent}
                personId={personId}
                t={t}
                logClick={logClick}
                onReply={() => setReply(strippedMessageText(message.text))}
              />
            ))}
            <div ref={bottomRef} />
          </>
        )}
      </ScrollArea>

      {/* The other honest answer to a message: nothing. Sits just above the composer and right-aligned,
          so the way OUT of a thread shares an edge with the way to answer it rather than facing it
          across the sheet. Hidden once the thread is settled — there is nothing left to mark. The same
          component the task modal and the full-page thread use, so the write, the failure line and the
          BI all stay in one place; only the label, the glyph and the alignment differ here. */}
      {!settled && !failed ? (
        <OutreachNoReplyNeeded
          outreachId={threadOutreachId}
          labelKey="contacts.viewer.markAsReplied"
          startIcon={<Check size={16} aria-hidden />}
          align="end"
          pageName="contacts_page"
          refresh={() => refreshContactData(contactId)}
          onCompleted={onSettled}
        />
      ) : null}

      {/* No composer over a read that FAILED: the thread we would be replying to was never loaded, so a
          send would go out against a target we could not confirm — and the empty transcript above it
          reads as "they said nothing", which is a different thing from "we could not look". */}
      {failed ? null : (
      <ViewerComposer
        personId={personId}
        recipientId={recipientId}
        outreachId={outreachId}
        myAgent={myAgent}
        reply={reply}
        onClearReply={() => setReply(null)}
        t={t}
        logClick={logClick}
      />
      )}
    </div>
  );
}

function MessageBubble({
  message,
  mine,
  showSender,
  awaitsYou,
  theirName,
  theirPhoto,
  myAgent,
  personId,
  t,
  logClick,
  onReply,
}: {
  message: WebOutreachMessage;
  mine: boolean;
  showSender: boolean;
  awaitsYou: boolean;
  theirName: string;
  theirPhoto?: string;
  myAgent: string;
  personId: string;
  t: TFn;
  logClick: LogClick;
  onReply: () => void;
}) {
  const body = strippedMessageText(message.text);
  const time = shortRelativeTime(message.sentAt, undefined, t('contacts.time.now'));

  if (mine) {
    return (
      <div className={styles.viewerRowMine}>
        {showSender ? (
          <span className={styles.viewerSenderMine}>
            <MaestroMark size={16} />
            <Typography variant="p3" color="secondary">
              {t('contacts.viewer.yourAgent', { agent: myAgent })}
            </Typography>
          </span>
        ) : null}
        <div className={styles.viewerBubbleMine}>
          <div dir="auto">
            <Markdown>{body}</Markdown>
          </div>
          <Typography variant="p3" color="secondary" className={styles.viewerBubbleTimeMine}>
            {time}
          </Typography>
        </div>
        {/* Files travel with the message here too — same rows/links as the full-page thread. */}
        <MessageAttachments attachments={message.attachments} />
      </div>
    );
  }

  return (
    <div className={styles.viewerRowTheirs}>
      {showSender ? (
        <span className={styles.viewerSenderTheirs}>
          <MaestroMark size={16} />
          <Avatar initials={avatarInitials(theirName)} photoUrl={theirPhoto} size={18} color={avatarSlotFor(personId || theirName)} />
          <Typography variant="p3" color="secondary">
            {/* The INCOMING side is the contact's reply — via THEIR Maestro, whose name we don't have.
                Never our own agent name (`myAgent`): a generic, localized "their Maestro" instead. */}
            {t('contacts.viewer.viaTheirAgent', { name: theirName })}
          </Typography>
          {awaitsYou ? <Badge variant="waiting-on-you" size="tiny" label={t('contacts.row.waitingOnYou')} /> : null}
        </span>
      ) : null}
      <div className={styles.viewerBubbleTheirs} dir="auto">
        <Markdown>{body}</Markdown>
      </div>
      <MessageAttachments attachments={message.attachments} />
      <div className={styles.viewerMsgFooter}>
        <span className={styles.viewerActions}>
          <IconButton size="tiny" variant="text" aria-label={t('chat.reply.action')} onClick={onReply}>
            <Reply size={16} aria-hidden />
          </IconButton>
          <IconButton
            size="tiny"
            variant="text"
            aria-label={t('common.copy')}
            onClick={() => {
              void navigator.clipboard?.writeText(body).catch(() => {});
              logClick({ page_name: 'contact_thread', button_name: 'copy_message' });
            }}
          >
            <Copy size={16} aria-hidden />
          </IconButton>
        </span>
        <Typography variant="p3" color="secondary">
          {time}
        </Typography>
      </div>
    </div>
  );
}

function ViewerComposer({
  personId,
  recipientId,
  outreachId,
  myAgent,
  reply,
  onClearReply,
  t,
  logClick,
}: {
  personId: string;
  recipientId?: string;
  outreachId: string;
  myAgent: string;
  reply: string | null;
  onClearReply: () => void;
  t: TFn;
  logClick: LogClick;
}) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);

  // Only a MISSING outreach makes the composer inert — there is nothing to send against. A closed
  // conversation is writable: the server reopens it on send (#10932, #10934).
  const noActiveThread = !outreachId;
  const canSend = text.trim().length > 0 && !busy && !noActiveThread;

  // Send the user's own words DIRECTLY on the outreach — no chat, no agent relay. The message is appended
  // optimistically so it shows the instant Send is pressed and the viewer STAYS open; on success
  // `refreshContact` reconciles against the server's own copy (same query key, so no duplicate), and on
  // failure the optimistic append rolls back and an inline error keeps the draft for a retry.
  const contactId: ContactId = personId ? { personId } : { recipientId: recipientId ?? '' };
  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed || busy || !outreachId) return;
    logClick({ page_name: 'contact_thread', button_name: 'send_outreach_reply' });
    setBusy(true);
    setFailed(false);
    // The optimistic append is person-cache-keyed; for an address-only contact we skip it and just
    // reconcile from the server echo (the send itself is by outreachId, identity-agnostic).
    const rollback = personId ? appendOptimisticOutreachMessage(personId, outreachId, trimmed) : () => {};
    setText('');
    onClearReply();
    const result = await sendOutreachMessage(outreachId, trimmed);
    setBusy(false);
    if (result === 'sent') {
      // The whole contact, not just this thread: the send REOPENED it if it was closed, so the row
      // badges and the section it sits under in the list are stale too.
      void refreshContactData(contactId);
      return;
    }
    rollback();
    // Restore ONLY if the field is still untouched — the composer stays editable while the send is in
    // flight, so an unconditional restore would overwrite a second draft the user began meanwhile with
    // the message that failed. Their newer words win. The updater form reads the CURRENT value rather
    // than the one this closure captured. 1:1 with iOS/Android.
    setText((current) => current || trimmed);
    setFailed(true);
  };

  // A text-only composer (attach / mic / Speak all removed for now): the rounded prompt box holds the
  // field and a round Send that appears once there is text.
  return (
    <form
      className={styles.viewerComposer}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
    >
      <div className={styles.viewerComposerBox}>
        {reply ? (
          <span className={styles.viewerReplyPreview}>
            <Typography variant="p3" color="secondary" className={styles.viewerReplyText}>
              {reply}
            </Typography>
            <IconButton size="tiny" variant="text" aria-label={t('common.cancel')} onClick={onClearReply}>
              <X size={16} aria-hidden />
            </IconButton>
          </span>
        ) : null}
        {/* The send failed (a refusal or a dropped request) — the draft is kept for a retry. No chat
            fallback: the whole correspondence lives in this sheet. */}
        {failed ? (
          <Typography variant="p3" className={styles.viewerSendError} role="alert">
            {t('contacts.viewer.sendFailed')}
          </Typography>
        ) : null}
        {/* Field + Send on ONE row (Send bottom-aligned): a single row until the text wraps, then the
            field grows and Send stays with the last line. Send is always shown, disabled while empty. */}
        <div className={styles.viewerComposerRow}>
          <textarea
            className={styles.viewerComposerField}
            value={text}
            disabled={noActiveThread}
            onChange={(e) => {
              setText(e.target.value);
              if (failed) setFailed(false);
            }}
            placeholder={t('contacts.viewer.composerPlaceholder', { agent: myAgent })}
            rows={1}
            onKeyDown={(e) => {
              // Enter sends; Shift+Enter is a newline. IME composition is left to complete.
              if (e.key === 'Enter' && !e.shiftKey && !isImeComposing(e)) {
                e.preventDefault();
                void submit();
              }
            }}
          />
          <IconButton size="small" variant="contained" color="standard" type="submit" disabled={!canSend} aria-label={t('common.send')}>
            <ArrowUp size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
          </IconButton>
        </div>
      </div>
    </form>
  );
}
